import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { ActionFunction, redirect } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import { getAgencyUser } from '~/api/data/users/queries';
import {
  CreateAgencyUsersDocument,
  CreateAgencyUsersMutation,
  CreateAgencyUsersMutationVariables,
  UpdateAgencyUserDocument,
  UpdateAgencyUserMutation,
  UpdateAgencyUserMutationVariables,
  CreateAgencyUserProducerInformationDocument,
  CreateAgencyUserProducerInformationMutation,
  CreateAgencyUserProducerInformationMutationVariables,
  SignOutDocument,
  SignOutMutation,
  SignOutMutationVariables,
  UpdateAgencyUserPreferencesDocument,
  UpdateAgencyUserPreferencesMutation,
  UpdateAgencyUserPreferencesMutationVariables,
  UpdateProducerInformationDocument,
  UpdateProducerInformationMutation,
  UpdateProducerInformationMutationVariables
} from '~/api/graphql';

export const createAgencyUsers = (
  variables: CreateAgencyUsersMutationVariables,
  apollo: ApolloClient<NormalizedCacheObject> = client
) =>
  mutateData<CreateAgencyUsersMutation, CreateAgencyUsersMutationVariables>(apollo, {
    mutation: CreateAgencyUsersDocument,
    variables
  });

export const updateAgencyUser = (
  variables: UpdateAgencyUserMutationVariables,
  apollo: ApolloClient<NormalizedCacheObject> = client
) =>
  mutateData<UpdateAgencyUserMutation, UpdateAgencyUserMutationVariables>(apollo, {
    mutation: UpdateAgencyUserDocument,
    variables
  });

export const signOut = () =>
  mutateData<SignOutMutation, SignOutMutationVariables>(client, { mutation: SignOutDocument });

export const updateAgencyUserPreferences = (
  variables: UpdateAgencyUserPreferencesMutationVariables
) =>
  mutateData<UpdateAgencyUserPreferencesMutation, UpdateAgencyUserPreferencesMutationVariables>(
    client,
    { mutation: UpdateAgencyUserPreferencesDocument, variables }
  );

export const createUserProducerInformation = (
  variables: CreateAgencyUserProducerInformationMutationVariables
) =>
  mutateData<
    CreateAgencyUserProducerInformationMutation,
    CreateAgencyUserProducerInformationMutationVariables
  >(client, {
    mutation: CreateAgencyUserProducerInformationDocument,
    variables
  });

export const updateUserProducerInformation = (
  variables: UpdateProducerInformationMutationVariables
) =>
  mutateData<UpdateProducerInformationMutation, UpdateProducerInformationMutationVariables>(
    client,
    {
      mutation: UpdateProducerInformationDocument,
      variables
    }
  );

export const submitUpdateAgencyUserPreferences: ActionFunction = async ({ request }) =>
  submitMutation(updateAgencyUserPreferences({ input: await request.json() }));

export const submitCreateUserProducerInformation: ActionFunction = async ({ request }) => {
  const agencyUserResult = await getAgencyUser();
  const agencyUserId = agencyUserResult.data?.id;
  if (!agencyUserId) {
    return { applicationError: { message: 'Unable to determine user ID' } };
  }
  return submitMutation(
    createUserProducerInformation({ agencyUserId, input: await request.json() }),
    async () => redirect('/app/settings/profile')
  );
};

export const submitUpdateUserProducerInformation: ActionFunction = async ({ params, request }) =>
  submitMutation(
    updateUserProducerInformation({ id: params.id || '', input: await request.json() }),
    async () => redirect('/app/settings/profile')
  );
